import { useState } from 'react';
import { useSelectedCompany } from '@features/company/hooks/useSelectedCompany';

/**
 * Cabecera del sidebar: el logo de la compania sobre una PLACA BLANCA, con el
 * nombre de la empresa al lado.
 *
 * El fondo blanco del archivo NO se quita: se asume. Los logos llegan como JPG
 * con fondo blanco y tinta pensada para papel, asi que la placa no es un
 * parche sino la superficie que el logo necesita para leerse sobre el azul.
 * Al coincidir el blanco del archivo con el de la placa, el recuadro deja de
 * existir como tal y lo que se ve es una tarjeta de marca.
 *
 * Existe `lib/utils/logoForDarkBackground` para el camino contrario —recortar
 * el fondo y apoyar el logo directo sobre el azul—, que quedo sin uso al
 * elegir esta version. Se conserva porque es la unica forma de tener el logo
 * sin ninguna superficie clara si en algun momento se vuelve a esa idea.
 *
 * La cabecera mide mas que la barra superior a proposito: la placa necesita
 * alto para que el logo se lea, y el limite de 64px lo dejaba diminuto.
 *
 * Sin logo, la placa se reemplaza por un MONOGRAMA con las iniciales de la
 * compania, del mismo tamaño. No se deja el hueco vacio ni una placa blanca
 * sin nada: lo primero descoloca el nombre y lo segundo se lee como una imagen
 * que no cargo.
 */

/** Alto de la cabecera colapsada (icono solo, centrado). */
const COLLAPSED_HEADER_HEIGHT = 84;

/**
 * Alto MÍNIMO de la cabecera expandida (logo/monograma + nombre debajo). Es
 * un piso, no un alto fijo: un nombre corto (1 línea) queda con este alto;
 * uno más largo que necesite la segunda línea del `line-clamp-2` de más
 * abajo crece un poco más, en vez de recortarse a la fuerza contra un alto
 * fijo. El `line-clamp-2` sigue siendo el techo — nunca pasa de 2 líneas de
 * nombre — así que el crecimiento tiene un límite conocido y no "deforma"
 * el sidebar de forma impredecible.
 */
const EXPANDED_HEADER_MIN_HEIGHT = 140;

/**
 * Iniciales de la compania para el monograma: hasta dos, saltando palabras
 * que no identifican ("de", "y", "S.A."). "Los Altos de Cerro Azul" -> "LA".
 */
const SKIP = new Set(['de', 'del', 'la', 'las', 'los', 'y', 'e', 'sa', 'srl', 'the']);
function initialsOf(name: string): string {
  const words = name
    .split(/\s+/)
    .map((w) => w.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter((w) => w && !SKIP.has(w.toLowerCase()));
  const pick = words.length ? words : name.split(/\s+/).filter(Boolean);
  return pick.slice(0, 2).map((w) => w[0]!.toUpperCase()).join('') || '?';
}

/**
 * Placa del logo: ALTO fijo (estándar, no crece), ANCHO automático según la
 * proporción real del logo — antes era una caja fija (96×52) que forzaba
 * todo logo a la misma forma: uno cuadrado quedaba perdido en el medio con
 * aire a los lados, uno panorámico se aplastaba para entrar. Con
 * `height` fijo + `width: auto` en la propia imagen, el navegador calcula el
 * ancho que le corresponde a CADA logo según su relación de aspecto real
 * (sin JS): uno cuadrado da una placa angosta, uno apaisado da una más
 * ancha. `maxWidth` es el límite para que un logo muy panorámico no
 * agrande la cabecera del sidebar más de la cuenta.
 */
const PLATE = { height: 52, maxWidth: 180 };
const COLLAPSED_PLATE = { height: 44, maxWidth: 64 };

export function SidebarCompanyLogo({ collapsed = false }: { collapsed?: boolean }) {
  const { data, isPending } = useSelectedCompany();
  const [failed, setFailed] = useState(false);

  const company = data?.company ?? null;
  const rawUrl = company?.profileImage?.trim() || '';
  const logoUrl = rawUrl && !failed ? rawUrl : '';
  const name = company?.name?.trim() || '';

  if (isPending && !data) {
    return (
      <div
        style={{ height: collapsed ? COLLAPSED_HEADER_HEIGHT : EXPANDED_HEADER_MIN_HEIGHT }}
        className="shrink-0 border-b border-white/[.08]"
        aria-hidden="true"
        data-testid="sidebar-company-loading"
      />
    );
  }

  // Colapsado: sin cambios — solo el icono, centrado, sin nombre.
  if (collapsed) {
    return (
      <div
        className="flex shrink-0 items-center justify-center border-b border-white/[.08] px-2"
        style={{ height: COLLAPSED_HEADER_HEIGHT }}
        data-testid="sidebar-company-logo"
      >
        {logoUrl ? (
          <span
            className="flex shrink-0 items-center justify-center rounded-lg bg-white shadow-[0_1px_3px_rgba(0,0,0,.3)]"
            style={{ height: COLLAPSED_PLATE.height, maxWidth: COLLAPSED_PLATE.maxWidth, padding: 5 }}
          >
            <img
              src={logoUrl}
              alt={`Logo de ${name}`}
              // `height:100%` + `width:auto` (no un box cuadrado fijo): el
              // ancho lo calcula el navegador según la proporción real del
              // logo, con `maxWidth` de la placa como techo.
              style={{ height: '100%', width: 'auto', maxWidth: '100%' }}
              className="object-contain"
              onError={() => setFailed(true)}
              data-testid="sidebar-company-logo-image"
            />
          </span>
        ) : (
          <span
            className="flex h-11 w-11 items-center justify-center rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 font-display text-[15px] font-extrabold text-white"
            aria-label={name}
            data-testid="sidebar-company-monogram"
          >
            {initialsOf(name)}
          </span>
        )}
      </div>
    );
  }

  // Expandido: logo/monograma arriba y el nombre COMPLETO debajo, no al lado.
  // Al lado solo tenía ~121px de ancho libre (260px de sidebar menos la placa
  // y el padding); debajo tiene todo el ancho del sidebar, así que nombres
  // largos entran sin cortarse en la gran mayoría de los casos. `minHeight`
  // (no `height`) deja que un nombre de 2 líneas empuje la cabecera un poco
  // más alta en vez de recortarse contra un alto fijo; `line-clamp-2` sigue
  // siendo el techo (nunca más de 2 líneas), con `title` como respaldo.
  return (
    <div
      className="flex shrink-0 flex-col items-center justify-center gap-3 border-b border-white/[.08] px-4 py-5"
      style={{ minHeight: EXPANDED_HEADER_MIN_HEIGHT }}
      data-testid="sidebar-company-logo"
    >
      {logoUrl ? (
        <span
          className="flex shrink-0 items-center justify-center rounded-lg bg-white shadow-[0_1px_3px_rgba(0,0,0,.3)]"
          style={{ height: PLATE.height, maxWidth: PLATE.maxWidth, padding: 5 }}
        >
          <img
            src={logoUrl}
            // El nombre ya esta en el DOM debajo: con `alt` descriptivo un
            // lector de pantalla anunciaria la compania dos veces seguidas.
            alt=""
            aria-hidden="true"
            // `height:100%` + `width:auto`: el ancho de la imagen (y por lo
            // tanto de la placa, que no tiene ancho propio) sale de la
            // proporción real del logo, no de una caja fija — ver PLATE.
            style={{ height: '100%', width: 'auto', maxWidth: '100%' }}
            className="object-contain"
            onError={() => setFailed(true)}
            data-testid="sidebar-company-logo-image"
          />
        </span>
      ) : (
        // Sin logo va un monograma CUADRADO (altura x altura): sin una
        // proporción real que seguir, un cuadrado es la forma más neutra.
        <span
          className="flex shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 font-display font-extrabold text-white"
          style={{ width: PLATE.height, height: PLATE.height, fontSize: 20, letterSpacing: '0.02em' }}
          aria-hidden="true"
          data-testid="sidebar-company-monogram"
        >
          {initialsOf(name)}
        </span>
      )}

      {/*
        Acento de marca (degradado teal→violeta, el mismo que ya usa el
        sistema en un solo lugar por pantalla) a modo de separador entre el
        logo y el nombre — le da estructura al bloque en vez de "logo +
        texto" sueltos uno debajo del otro.
      */}
      <span aria-hidden="true" className="h-[3px] w-8 rounded-full bg-gradient-to-r from-teal-400 to-[#8b5cf6]" />

      {/*
        Nombre con más presencia que un bold gris parejo: blanco puro con una
        sombra suave (lo despega del fondo con textura de grano) y tracking
        apenas abierto, que en textos cortos en mayúscula/minúscula normal
        lee más "cuidado" que el tracking apretado por defecto.
      */}
      <span
        className="line-clamp-2 w-full min-w-0 text-center font-display text-[13.5px] font-semibold leading-[1.4] tracking-[0.01em] text-white"
        style={{ textShadow: '0 1px 3px rgba(0,0,0,.35)' }}
        title={name}
        data-testid="sidebar-company-name"
      >
        {name || 'Facturación Electrónica'}
      </span>
    </div>
  );
}
